import type {
  DashboardDecorationKind,
  DashboardDecorationPlacement,
  DashboardDecorationStyle,
  DashboardVisualPlacement,
  DashboardWidgetPlacement,
} from "../types/dashboard";
import type { LayoutItem } from "react-grid-layout";

export type DecorationCatalogEntry = {
  kind: DashboardDecorationKind;
  icon: string;
  labelKey: string;
  descriptionKey: string;
  defaultW: number;
  defaultH: number;
  minW: number;
  minH: number;
  defaultTitle?: string;
  defaultContent?: string;
  defaultStyle?: DashboardDecorationStyle;
};

export const DECORATION_DRAG_MIME = "application/x-dashboard-decoration-kind";

export const DECORATION_CATALOG: DecorationCatalogEntry[] = [
  {
    kind: "heading",
    icon: "title",
    labelKey: "dashboard.decor.kind.heading",
    descriptionKey: "dashboard.decor.desc.heading",
    defaultW: 12,
    defaultH: 2,
    minW: 2,
    minH: 1,
    defaultTitle: "Section title",
    defaultStyle: { fontSize: 24, fontWeight: 700, fontColor: "#1e293b", textAlign: "left", padding: 8 },
  },
  {
    kind: "label",
    icon: "label",
    labelKey: "dashboard.decor.kind.label",
    descriptionKey: "dashboard.decor.desc.label",
    defaultW: 4,
    defaultH: 1,
    minW: 1,
    minH: 1,
    defaultTitle: "Label",
    defaultStyle: { fontSize: 14, fontWeight: 600, fontColor: "#475569", textAlign: "left", padding: 6 },
  },
  {
    kind: "panel",
    icon: "crop_square",
    labelKey: "dashboard.decor.kind.panel",
    descriptionKey: "dashboard.decor.desc.panel",
    defaultW: 6,
    defaultH: 3,
    minW: 2,
    minH: 1,
    defaultTitle: "Panel title",
    defaultContent: "Panel body text or notes.",
    defaultStyle: {
      fontSize: 14,
      fontColor: "#334155",
      backgroundColor: "#ffffff",
      borderColor: "#cbd5e1",
      borderWidth: 1,
      borderStyle: "solid",
      borderRadius: 8,
      padding: 12,
    },
  },
  {
    kind: "text",
    icon: "notes",
    labelKey: "dashboard.decor.kind.text",
    descriptionKey: "dashboard.decor.desc.text",
    defaultW: 6,
    defaultH: 2,
    minW: 2,
    minH: 1,
    defaultContent: "Free-form text block for descriptions, footnotes, or KPI context.",
    defaultStyle: { fontSize: 14, fontColor: "#475569", textAlign: "left", padding: 8 },
  },
  {
    kind: "table",
    icon: "table_chart",
    labelKey: "dashboard.decor.kind.table",
    descriptionKey: "dashboard.decor.desc.table",
    defaultW: 8,
    defaultH: 3,
    minW: 2,
    minH: 1,
    defaultTitle: "Summary table",
    defaultContent: JSON.stringify({
      headers: ["Metric", "Value"],
      rows: [
        ["Orders", "1,240"],
        ["Revenue", "₺98K"],
      ],
    }),
    defaultStyle: {
      fontSize: 13,
      fontColor: "#1e293b",
      backgroundColor: "#ffffff",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      borderStyle: "solid",
      borderRadius: 6,
      padding: 8,
    },
  },
  {
    kind: "image",
    icon: "image",
    labelKey: "dashboard.decor.kind.image",
    descriptionKey: "dashboard.decor.desc.image",
    defaultW: 4,
    defaultH: 3,
    minW: 1,
    minH: 1,
    defaultTitle: "Image",
    defaultStyle: {
      borderColor: "#e2e8f0",
      borderWidth: 1,
      borderRadius: 8,
      objectFit: "cover",
      imageUrl: "",
    },
  },
  {
    kind: "divider",
    icon: "horizontal_rule",
    labelKey: "dashboard.decor.kind.divider",
    descriptionKey: "dashboard.decor.desc.divider",
    defaultW: 12,
    defaultH: 1,
    minW: 2,
    minH: 1,
    defaultStyle: { borderColor: "#cbd5e1", dividerThickness: 2, padding: 4 },
  },
  {
    kind: "spacer",
    icon: "space_bar",
    labelKey: "dashboard.decor.kind.spacer",
    descriptionKey: "dashboard.decor.desc.spacer",
    defaultW: 12,
    defaultH: 1,
    minW: 1,
    minH: 1,
    defaultStyle: { opacity: 0 },
  },
];

export function catalogEntryFor(kind: DashboardDecorationKind): DecorationCatalogEntry {
  return DECORATION_CATALOG.find((e) => e.kind === kind) ?? DECORATION_CATALOG[0];
}

export function newDecorationId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `decor-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createDecoration(
  kind: DashboardDecorationKind,
  position?: { x: number; y: number; w: number; h: number }
): DashboardDecorationPlacement {
  const def = catalogEntryFor(kind);
  return {
    id: newDecorationId(),
    kind,
    x: position?.x ?? 0,
    y: position?.y ?? Infinity,
    w: position?.w ?? def.defaultW,
    h: position?.h ?? def.defaultH,
    minW: def.minW,
    minH: def.minH,
    title: def.defaultTitle,
    content: def.defaultContent,
    style: { ...def.defaultStyle },
  };
}

export type StaticTableData = { headers: string[]; rows: string[][] };

export function parseStaticTable(content?: string): StaticTableData {
  if (!content?.trim()) {
    return { headers: ["Column 1", "Column 2"], rows: [["", ""]] };
  }
  try {
    const parsed = JSON.parse(content) as StaticTableData;
    if (Array.isArray(parsed.headers) && Array.isArray(parsed.rows)) {
      return {
        headers: parsed.headers.map(String),
        rows: parsed.rows.map((r) => (Array.isArray(r) ? r.map(String) : [])),
      };
    }
  } catch {
    /* fallback */
  }
  return { headers: ["Column 1", "Column 2"], rows: [["", ""]] };
}

export function stringifyStaticTable(data: StaticTableData): string {
  return JSON.stringify(data);
}

export function normalizeDecorationPlacement(d: DashboardDecorationPlacement): DashboardDecorationPlacement {
  const kind = (d.kind || "panel") as DashboardDecorationKind;
  const def = catalogEntryFor(kind);
  return {
    ...d,
    id: String(d.id),
    kind,
    h: d.h > 0 ? d.h : def.defaultH,
    w: d.w > 0 ? d.w : def.defaultW,
    minH: def.minH,
    minW: def.minW,
    title: d.title ?? def.defaultTitle,
    content: d.content ?? def.defaultContent,
    style: { ...def.defaultStyle, ...d.style },
  };
}

export function normalizeDecorations(
  decorations: DashboardDecorationPlacement[] | undefined
): DashboardDecorationPlacement[] {
  return (decorations ?? []).map(normalizeDecorationPlacement);
}

export const widgetGridKey = (widgetId: string) => `w:${widgetId}`;
export const decorationGridKey = (id: string) => `d:${String(id)}`;
export const visualGridKey = (id: string) => `v:${String(id)}`;

export function isWidgetGridKey(key: string): boolean {
  return key.startsWith("w:");
}

export function isDecorationGridKey(key: string): boolean {
  return key.startsWith("d:");
}

export function isVisualGridKey(key: string): boolean {
  return key.startsWith("v:");
}

export function mergeGridLayout(
  widgets: DashboardWidgetPlacement[],
  decorations: DashboardDecorationPlacement[],
  cols: number,
  editable: boolean,
  visuals: DashboardVisualPlacement[] = []
): LayoutItem[] {
  const all = [
    ...widgets.map((p) => ({ type: "widget" as const, p })),
    ...decorations.map((d) => ({ type: "decoration" as const, d })),
    ...visuals.map((v) => ({ type: "visual" as const, v })),
  ];

  if (cols === 1) {
    const yOf = (entry: (typeof all)[number]) =>
      entry.type === "widget" ? entry.p.y : entry.type === "decoration" ? entry.d.y : entry.v.y;
    const xOf = (entry: (typeof all)[number]) =>
      entry.type === "widget" ? entry.p.x : entry.type === "decoration" ? entry.d.x : entry.v.x;
    const sorted = [...all].sort((a, b) => yOf(a) - yOf(b) || xOf(a) - xOf(b));
    let y = 0;
    return sorted.map((entry) => {
      const h = entry.type === "widget" ? entry.p.h : entry.type === "decoration" ? entry.d.h : entry.v.h;
      const minH = entry.type === "decoration" ? catalogEntryFor(entry.d.kind).minH : 2;
      const item: LayoutItem = {
        i:
          entry.type === "widget"
            ? widgetGridKey(entry.p.widgetId)
            : entry.type === "decoration"
              ? decorationGridKey(String(entry.d.id))
              : visualGridKey(String(entry.v.id)),
        x: 0,
        y,
        w: 1,
        h,
        minH,
        minW: 1,
        static: !editable,
      };
      y += h;
      return item;
    });
  }

  const items: LayoutItem[] = [];
  for (const p of widgets) {
    items.push({
      i: widgetGridKey(p.widgetId),
      x: p.x,
      y: p.y,
      w: Math.min(p.w, cols),
      h: p.h,
      minW: Math.min(p.minW ?? 2, cols),
      minH: p.minH ?? 2,
      static: !editable,
    });
  }
  for (const d of decorations) {
    const def = catalogEntryFor(d.kind);
    items.push({
      i: decorationGridKey(String(d.id)),
      x: d.x,
      y: d.y,
      w: Math.min(d.w, cols),
      h: d.h,
      minW: Math.min(def.minW, cols),
      minH: def.minH,
      static: !editable,
    });
  }
  for (const v of visuals) {
    items.push({
      i: visualGridKey(String(v.id)),
      x: v.x,
      y: v.y,
      w: Math.min(v.w, cols),
      h: v.h,
      minW: Math.min(v.minW ?? 3, cols),
      minH: v.minH ?? 3,
      static: !editable,
    });
  }
  return items;
}

export function applyGridLayoutChange(
  widgets: DashboardWidgetPlacement[],
  decorations: DashboardDecorationPlacement[],
  next: readonly LayoutItem[],
  visuals: DashboardVisualPlacement[] = []
): {
  widgets: DashboardWidgetPlacement[];
  decorations: DashboardDecorationPlacement[];
  visuals: DashboardVisualPlacement[];
} {
  const updatedWidgets = widgets.map((p) => {
    const l = next.find((x) => x.i === widgetGridKey(p.widgetId));
    if (!l) return p;
    return { ...p, x: l.x, y: l.y, w: l.w, h: l.h };
  });
  const updatedDecorations = decorations.map((d) => {
    const l = next.find((x) => x.i === decorationGridKey(String(d.id)));
    if (!l) return d;
    const def = catalogEntryFor(d.kind);
    return { ...d, x: l.x, y: l.y, w: l.w, h: l.h, minH: def.minH, minW: def.minW };
  });
  const updatedVisuals = visuals.map((v) => {
    const l = next.find((x) => x.i === visualGridKey(String(v.id)));
    if (!l) return v;
    return { ...v, x: l.x, y: l.y, w: l.w, h: l.h };
  });
  return { widgets: updatedWidgets, decorations: updatedDecorations, visuals: updatedVisuals };
}
